import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { skillApi, type Skill } from '@/lib/api';
import { useNotification } from '@/contexts/NotificationContext';
import {
  BookOpen, Webhook, Code2, Zap, Search, Plus, Filter,
  Shield, ShieldCheck, Clock, Users, ArrowRight, Archive,
  CheckCircle2, AlertTriangle, XCircle,
} from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { PageGuide } from '@/components/PageGuide';



export const Route = createFileRoute('/_auth/skills/')(
  { component: SkillsPage },
);

// ── Constants ────────────────────────────────────────────────────────────

const IMPL_META: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  KNOWLEDGE_RETRIEVAL: { label: 'Knowledge',   icon: <BookOpen  size={14} />, color: '#06b6d4' },
  EXTERNAL_API:        { label: 'External API', icon: <Webhook  size={14} />, color: '#8b5cf6' },
  CODE:                { label: 'Code',         icon: <Code2    size={14} />, color: '#f59e0b' },
  PROMPT_ONLY:         { label: 'Prompt',       icon: <Zap      size={14} />, color: '#10b981' },
};

const RISK_META: Record<string, { color: string; bg: string; icon: React.ReactNode }> = {
  LOW:      { color: '#10b981', bg: '#10b98115', icon: <CheckCircle2 size={11} /> },
  MEDIUM:   { color: '#f59e0b', bg: '#f59e0b15', icon: <AlertTriangle size={11} /> },
  HIGH:     { color: '#ef4444', bg: '#ef444415', icon: <XCircle size={11} /> },
  CRITICAL: { color: '#dc2626', bg: '#dc262615', icon: <XCircle size={11} /> },
};

const FILTER_TYPES = ['ALL', 'KNOWLEDGE_RETRIEVAL', 'EXTERNAL_API', 'CODE', 'PROMPT_ONLY'] as const;
type FilterType = typeof FILTER_TYPES[number];

// ── Create Skill Form ─────────────────────────────────────────────────────

const BLANK_FORM = {
  name: '',
  description: '',
  implementationType: 'PROMPT_ONLY' as Skill['implementationType'],
  riskLevel: 'LOW' as Skill['riskLevel'],
  approvalRequired: false,
  systemPromptInstructions: '',
  toolName: '',
  maxExecutionsPerConversation: '',
  maxExecutionsPerHour: '',
  authRequirement: 'NONE' as Skill['authRequirement'],
};

function CreateSkillSheet({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { success, error: notifyError } = useNotification();
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof typeof BLANK_FORM>(k: K, v: (typeof BLANK_FORM)[K]) =>
    setForm(f => ({ ...f, [k]: v }));

  async function handleCreate() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const skill = await skillApi.create({
        ...form,
        maxExecutionsPerConversation: form.maxExecutionsPerConversation ? Number(form.maxExecutionsPerConversation) : undefined,
        maxExecutionsPerHour: form.maxExecutionsPerHour ? Number(form.maxExecutionsPerHour) : undefined,
        toolName: form.toolName || undefined,
        systemPromptInstructions: form.systemPromptInstructions || undefined,
      } as Partial<Skill>);
      await qc.invalidateQueries({ queryKey: ['skills'] });
      success('Skill created', `"${skill.name}" is ready to attach to agents.`);
      onClose();
      void navigate({ to: '/skills/$skillId', params: { skillId: skill.id } });
    } catch (e) {
      notifyError('Failed to create skill', (e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const implColor = IMPL_META[form.implementationType]?.color ?? '#6366f1';

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          zIndex: 900, backdropFilter: 'blur(4px)',
        }}
      />
      {/* Sheet */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 480,
        background: 'var(--surface-1)',
        borderLeft: '1px solid var(--border)',
        zIndex: 901, display: 'flex', flexDirection: 'column',
        boxShadow: '-24px 0 80px rgba(0,0,0,0.4)',
        animation: 'sheet-in 0.25s cubic-bezier(.4,0,.2,1)',
      }}>
        {/* Header */}
        <div style={{ padding: '24px 28px 20px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>New Skill</h2>
              <p style={{ fontSize: 12, color: 'var(--color-muted)', marginTop: 4 }}>
                Define a capability your agents can invoke during conversations
              </p>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', fontSize: 20, padding: 4 }}>×</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>

          {/* Type selector */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Skill Type</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {Object.entries(IMPL_META).map(([type, meta]) => {
                const active = form.implementationType === type;
                return (
                  <button
                    key={type}
                    onClick={() => set('implementationType', type as Skill['implementationType'])}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: `1.5px solid ${active ? meta.color : 'var(--border)'}`,
                      background: active ? `${meta.color}15` : 'var(--surface-2)',
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 8,
                      color: active ? meta.color : 'var(--color-muted)',
                      transition: 'all 0.15s',
                      fontWeight: active ? 600 : 400,
                      fontSize: 13,
                    }}
                  >
                    <span style={{ color: meta.color }}>{meta.icon}</span>
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          <Field label="Name *">
            <input
              id="skill-name"
              style={inputStyle}
              placeholder="e.g. Search knowledge base, Send email, Lookup order…"
              value={form.name}
              onChange={e => set('name', e.target.value)}
            />
          </Field>

          <Field label="Description">
            <textarea
              style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
              placeholder="Describe what this skill does and when the agent should use it…"
              value={form.description}
              onChange={e => set('description', e.target.value)}
            />
          </Field>

          <Field label="System Prompt Instructions">
            <textarea
              style={{ ...inputStyle, resize: 'vertical', minHeight: 80, fontFamily: 'monospace', fontSize: 12 }}
              placeholder="Instructions injected into the system prompt telling the agent how/when to invoke this skill…"
              value={form.systemPromptInstructions}
              onChange={e => set('systemPromptInstructions', e.target.value)}
            />
            <span style={{ fontSize: 11, color: 'var(--color-muted)', marginTop: 4 }}>
              This text is automatically added to the agent's system prompt when the skill is attached.
            </span>
          </Field>

          {(form.implementationType === 'EXTERNAL_API' || form.implementationType === 'CODE') && (
            <Field label="Tool Name">
              <input
                style={inputStyle}
                placeholder="snake_case function name, e.g. search_knowledge"
                value={form.toolName}
                onChange={e => set('toolName', e.target.value)}
              />
            </Field>
          )}

          {/* Risk & Approval */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            <Field label="Risk Level">
              <select
                style={inputStyle}
                value={form.riskLevel}
                onChange={e => set('riskLevel', e.target.value as Skill['riskLevel'])}
              >
                {Object.keys(RISK_META).map(r => <option key={r} value={r}>{r.charAt(0) + r.slice(1).toLowerCase()}</option>)}
              </select>
            </Field>
            <Field label="Auth Requirement">
              <select
                style={inputStyle}
                value={form.authRequirement}
                onChange={e => set('authRequirement', e.target.value as Skill['authRequirement'])}
              >
                <option value="NONE">None</option>
                <option value="OPTIONAL">Optional</option>
                <option value="REQUIRED">Required</option>
              </select>
            </Field>
          </div>

          {/* Rate limits */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            <Field label="Max executions / conversation">
              <input type="number" style={inputStyle} min={1} placeholder="Unlimited"
                value={form.maxExecutionsPerConversation}
                onChange={e => set('maxExecutionsPerConversation', e.target.value)} />
            </Field>
            <Field label="Max executions / hour">
              <input type="number" style={inputStyle} min={1} placeholder="Unlimited"
                value={form.maxExecutionsPerHour}
                onChange={e => set('maxExecutionsPerHour', e.target.value)} />
            </Field>
          </div>

          {/* Approval toggle */}
          <div style={{
            padding: '14px 16px', borderRadius: 10,
            border: `1px solid ${form.approvalRequired ? '#6366f133' : 'var(--border)'}`,
            background: form.approvalRequired ? '#6366f108' : 'var(--surface-2)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            cursor: 'pointer',
          }} onClick={() => set('approvalRequired', !form.approvalRequired)}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13, color: form.approvalRequired ? '#818cf8' : 'var(--color-text)' }}>
                <ShieldCheck size={14} /> Requires human approval
              </div>
              <p style={{ fontSize: 11, color: 'var(--color-muted)', marginTop: 3 }}>
                Agent must request approval before executing this skill
              </p>
            </div>
            <div style={{
              width: 40, height: 22, borderRadius: 11,
              background: form.approvalRequired ? '#6366f1' : 'var(--surface-3)',
              position: 'relative', transition: 'background 0.2s',
            }}>
              <div style={{
                position: 'absolute', top: 3, left: form.approvalRequired ? 21 : 3,
                width: 16, height: 16, borderRadius: '50%', background: '#fff',
                transition: 'left 0.2s',
              }} />
            </div>
          </div>

          {/* Risk level preview */}
          {form.riskLevel !== 'LOW' && (
            <div style={{
              marginTop: 16, padding: '12px 14px', borderRadius: 8,
              background: `${RISK_META[form.riskLevel]?.bg}`,
              border: `1px solid ${RISK_META[form.riskLevel]?.color}33`,
              color: RISK_META[form.riskLevel]?.color,
              fontSize: 12, display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {RISK_META[form.riskLevel]?.icon}
              <span>
                <strong>{form.riskLevel} RISK</strong> — {form.approvalRequired ? 'Approval is required ✓' : 'Consider enabling approval for this risk level.'}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 28px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={ghostBtnStyle}>Cancel</button>
          <button
            id="create-skill-submit"
            onClick={handleCreate}
            disabled={saving || !form.name.trim()}
            style={{
              padding: '10px 20px', borderRadius: 8, border: 'none',
              background: implColor, color: '#fff',
              fontWeight: 600, fontSize: 14, cursor: 'pointer',
              opacity: saving || !form.name.trim() ? 0.5 : 1,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            {saving ? 'Creating…' : <><Plus size={14} /> Create & Configure</>}
          </button>
        </div>
      </div>
      <style>{`
        @keyframes sheet-in {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────

export default function SkillsPage() {
  const qc = useQueryClient();
  const { success, error: notifyError } = useNotification();
  const { data: skills = [] as Skill[], isLoading } = useQuery({ queryKey: ['skills'], queryFn: skillApi.list });
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('ALL');
  const [filterRisk, setFilterRisk] = useState('');

  const archiveMutation = useMutation({
    mutationFn: (id: string) => skillApi.archive(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['skills'] });
      success('Skill archived');
    },
    onError: (e) => notifyError('Failed to archive skill', (e as Error).message),
  });

  const filtered = useMemo(() =>
    skills.filter(s => {
      if (filterType !== 'ALL' && s.implementationType !== filterType) return false;
      if (filterRisk && s.riskLevel !== filterRisk) return false;
      if (search) {
        const q = search.toLowerCase();
        return s.name.toLowerCase().includes(q) ||
          s.description?.toLowerCase().includes(q) ||
          s.toolName?.toLowerCase().includes(q);
      }
      return true;
    })
  , [skills, search, filterType, filterRisk]);

  // Stats
  const stats = useMemo(() => ({
    total: skills.length,
    withApproval: skills.filter(s => s.approvalRequired).length,
    agentsUsing: skills.reduce((acc, s) => acc + (s._count?.agentConfigs ?? 0), 0),
    byType: Object.fromEntries(
      Object.keys(IMPL_META).map(t => [t, skills.filter(s => s.implementationType === t).length])
    ),
  }), [skills]);

  return (
    <div className="page">
      <PageGuide pageKey="skills" />
      {showCreate && <CreateSkillSheet onClose={() => setShowCreate(false)} />}

      {/* ── Header ── */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, letterSpacing: -0.5 }}>Skill Library</h1>
            <p style={{ color: 'var(--color-muted)', fontSize: 13, marginTop: 6 }}>
              Define capabilities your agents can invoke — API calls, knowledge retrieval, code execution and more
            </p>
          </div>
          <button
            id="create-skill-btn"
            onClick={() => setShowCreate(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 18px', borderRadius: 10, border: 'none',
              background: 'var(--color-primary)', color: '#fff',
              fontWeight: 600, fontSize: 14, cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(99,102,241,0.3)',
              transition: 'transform 0.15s, box-shadow 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(99,102,241,0.4)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 4px 16px rgba(99,102,241,0.3)'; }}
          >
            <Plus size={15} /> New Skill
          </button>
        </div>

        {/* Stats row */}
        {skills.length > 0 && (
          <div style={{ display: 'flex', gap: 16, marginTop: 20, flexWrap: 'wrap' }}>
            {[
              { label: 'Total Skills', value: stats.total, color: '#6366f1' },
              { label: 'Needs Approval', value: stats.withApproval, color: '#f59e0b' },
              { label: 'Agent Attachments', value: stats.agentsUsing, color: '#10b981' },
              ...Object.entries(IMPL_META).map(([t, m]) => ({ label: m.label, value: stats.byType[t] ?? 0, color: m.color })),
            ].map(s => (
              <div key={s.label} style={{
                padding: '10px 16px', borderRadius: 10,
                background: `${s.color}10`, border: `1px solid ${s.color}25`,
                minWidth: 100,
              }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: 'var(--color-muted)', marginTop: 1 }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-muted)' }} />
          <input
            id="skill-search"
            style={{ ...inputStyle, paddingLeft: 36, width: '100%' }}
            placeholder="Search skills by name, description, tool…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* Type filter tabs */}
        <div style={{ display: 'flex', background: 'var(--surface-2)', borderRadius: 8, padding: 3, border: '1px solid var(--border)' }}>
          {FILTER_TYPES.map(t => {
            const meta = t === 'ALL' ? null : IMPL_META[t];
            const active = filterType === t;
            return (
              <button
                key={t}
                onClick={() => setFilterType(t)}
                style={{
                  padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                  background: active ? 'var(--surface-3)' : 'transparent',
                  color: active ? (meta?.color ?? 'var(--color-text)') : 'var(--color-muted)',
                  fontSize: 12, fontWeight: active ? 600 : 400,
                  display: 'flex', alignItems: 'center', gap: 5,
                  transition: 'all 0.15s',
                }}
              >
                {meta && <span style={{ color: meta.color }}>{meta.icon}</span>}
                {t === 'ALL' ? 'All' : meta?.label ?? t}
              </button>
            );
          })}
        </div>

        {/* Risk filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-muted)', fontSize: 12 }}>
          <Filter size={13} />
          <select
            style={{ ...inputStyle, padding: '5px 10px', fontSize: 12 }}
            value={filterRisk}
            onChange={e => setFilterRisk(e.target.value)}
          >
            <option value="">All risks</option>
            {Object.keys(RISK_META).map(r => <option key={r} value={r}>{r.charAt(0) + r.slice(1).toLowerCase()} risk</option>)}
          </select>
        </div>
      </div>

      {/* ── Content ── */}
      {isLoading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{ height: 170, borderRadius: 14, background: 'var(--surface-2)', opacity: 0.4, animation: 'pulse 1.5s ease infinite' }} />
          ))}
        </div>
      ) : skills.length === 0 ? (
        <EmptyState onCreate={() => setShowCreate(true)} />
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--color-muted)' }}>
          <Search size={32} style={{ margin: '0 auto 12px', display: 'block', opacity: 0.3 }} />
          <p style={{ fontWeight: 600 }}>No skills match your filters</p>
          <button style={{ ...ghostBtnStyle, marginTop: 12 }} onClick={() => { setSearch(''); setFilterType('ALL'); setFilterRisk(''); }}>
            Clear filters
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
          {filtered.map(skill => <SkillCard key={skill.id} skill={skill} onArchive={id => archiveMutation.mutate(id)} />)}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
}

// ── Skill Card ────────────────────────────────────────────────────────────

function SkillCard({ skill, onArchive }: { skill: Skill; onArchive: (id: string) => void }) {
  const impl = IMPL_META[skill.implementationType];
  const risk = RISK_META[skill.riskLevel];
  const [hovered, setHovered] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'var(--surface-2)',
        border: `1px solid ${hovered ? impl.color + '55' : 'var(--border)'}`,
        borderRadius: 14,
        padding: '18px 20px',
        transition: 'border-color 0.2s, transform 0.15s, box-shadow 0.2s',
        transform: hovered ? 'translateY(-2px)' : 'none',
        boxShadow: hovered ? `0 8px 32px ${impl.color}18` : 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      {/* Top row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 9,
            background: `${impl.color}18`,
            border: `1px solid ${impl.color}30`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: impl.color,
          }}>
            {impl.icon}
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: impl.color, fontWeight: 600 }}>{impl.label}</span>
              {skill.approvalRequired && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: '#818cf8', background: '#6366f115', border: '1px solid #6366f130', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>
                  <ShieldCheck size={9} /> APPROVAL
                </span>
              )}
            </div>
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, lineHeight: 1.3, marginTop: 2 }}>{skill.name}</h3>
          </div>
        </div>
        <span style={{
          display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700,
          color: risk.color, background: risk.bg,
          border: `1px solid ${risk.color}30`, borderRadius: 5, padding: '2px 7px',
          flexShrink: 0,
        }}>
          {risk.icon} {skill.riskLevel}
        </span>
      </div>

      {/* Description */}
      {skill.description && (
        <p style={{ fontSize: 13, color: 'var(--color-muted)', lineHeight: 1.5, margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {skill.description}
        </p>
      )}

      {/* System prompt preview */}
      {skill.systemPromptInstructions && (
        <div style={{
          padding: '8px 10px', borderRadius: 7,
          background: 'var(--surface-3)',
          border: '1px solid var(--border)',
          fontSize: 11, color: '#818cf8', fontStyle: 'italic', lineHeight: 1.4,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          "{skill.systemPromptInstructions}"
        </div>
      )}

      {/* Rate limits */}
      {(skill.maxExecutionsPerConversation || skill.maxExecutionsPerHour) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {skill.maxExecutionsPerConversation && (
            <span style={{ fontSize: 10, color: 'var(--color-muted)', display: 'flex', alignItems: 'center', gap: 3, background: 'var(--surface-3)', padding: '2px 7px', borderRadius: 4 }}>
              <Clock size={9} /> {skill.maxExecutionsPerConversation}× per conv
            </span>
          )}
          {skill.maxExecutionsPerHour && (
            <span style={{ fontSize: 10, color: 'var(--color-muted)', display: 'flex', alignItems: 'center', gap: 3, background: 'var(--surface-3)', padding: '2px 7px', borderRadius: 4 }}>
              <Clock size={9} /> {skill.maxExecutionsPerHour}× per hr
            </span>
          )}
        </div>
      )}

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: 8, borderTop: '1px solid var(--border)' }}>
        {/* Agent count — orange warning when > 0 */}
        {(skill._count?.agentConfigs ?? 0) > 0 ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#f59e0b', fontWeight: 600, background: '#f59e0b12', border: '1px solid #f59e0b30', borderRadius: 5, padding: '2px 8px' }}>
            <Users size={11} /> {skill._count!.agentConfigs} agent{skill._count!.agentConfigs !== 1 ? 's' : ''} — in use
          </span>
        ) : (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--color-muted)' }}>
            <Users size={11} /> Not attached
          </span>
        )}
        <div style={{ display: 'flex', gap: 6 }}>
          {/* Archive — disabled with tooltip when in use */}
          <span title={(skill._count?.agentConfigs ?? 0) > 0 ? `Detach from all agents before archiving` : undefined}>
            <button
              id={`archive-skill-${skill.id}`}
              disabled={(skill._count?.agentConfigs ?? 0) > 0}
              onClick={() => setArchiveConfirmOpen(true)}
              style={{
                ...ghostBtnStyle,
                padding: '4px 10px', fontSize: 11,
                color: (skill._count?.agentConfigs ?? 0) > 0 ? 'var(--color-muted)' : '#ef4444',
                borderColor: (skill._count?.agentConfigs ?? 0) > 0 ? 'var(--border)' : '#ef444433',
                cursor: (skill._count?.agentConfigs ?? 0) > 0 ? 'not-allowed' : 'pointer',
                opacity: (skill._count?.agentConfigs ?? 0) > 0 ? 0.45 : 1,
              }}
            >
              <Archive size={11} /> Archive
            </button>
          </span>
          <Link
            to="/skills/$skillId"
            params={{ skillId: skill.id }}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '4px 12px', borderRadius: 7,
              background: `${impl.color}18`, border: `1px solid ${impl.color}35`,
              color: impl.color, fontSize: 12, fontWeight: 600,
              textDecoration: 'none', transition: 'background 0.15s',
            }}
          >
            Configure <ArrowRight size={11} />
          </Link>
        </div>
      </div>

      <ConfirmDialog
        open={archiveConfirmOpen}
        onCancel={() => setArchiveConfirmOpen(false)}
        onConfirm={() => { setArchiveConfirmOpen(false); onArchive(skill.id); }}
        title={`Archive "${skill.name}"?`}
        description="This will deactivate the skill. It cannot be undone."
        confirmLabel="Archive Skill"
        variant="danger"
      />
    </div>
  );
}

// ── Empty State ────────────────────────────────────────────────────────────

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div style={{ textAlign: 'center', padding: '5rem 2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginBottom: 24 }}>
        {Object.values(IMPL_META).map((m, i) => (
          <div key={i} style={{ width: 48, height: 48, borderRadius: 14, background: `${m.color}18`, border: `1px solid ${m.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: m.color, fontSize: 20, animation: `float ${1.5 + i * 0.2}s ease-in-out infinite alternate` }}>
            {m.icon}
          </div>
        ))}
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 10px' }}>Build your skill library</h2>
      <p style={{ color: 'var(--color-muted)', maxWidth: 440, margin: '0 auto 28px', lineHeight: 1.6, fontSize: 14 }}>
        Skills are reusable capabilities — knowledge retrieval, external API calls, code execution, or custom prompts — that your agents can invoke during conversations.
      </p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 32 }}>
        {[
          { icon: <BookOpen size={14} />, label: 'Search docs & knowledge', color: '#06b6d4' },
          { icon: <Webhook size={14} />, label: 'Call external APIs', color: '#8b5cf6' },
          { icon: <Code2 size={14} />, label: 'Run custom code', color: '#f59e0b' },
          { icon: <Shield size={14} />, label: 'Human approval flows', color: '#10b981' },
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 14px', borderRadius: 8, background: `${item.color}12`, border: `1px solid ${item.color}25`, color: item.color, fontSize: 12, fontWeight: 500 }}>
            {item.icon} {item.label}
          </div>
        ))}
      </div>
      <button
        id="create-first-skill"
        onClick={onCreate}
        style={{
          padding: '12px 28px', borderRadius: 10, border: 'none',
          background: 'var(--color-primary)', color: '#fff',
          fontWeight: 700, fontSize: 15, cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(99,102,241,0.35)',
          display: 'inline-flex', alignItems: 'center', gap: 8,
        }}
      >
        <Plus size={16} /> Create your first skill
      </button>
      <style>{`
        @keyframes float {
          from { transform: translateY(0); }
          to   { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  padding: '9px 12px', borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface-2)',
  color: 'var(--color-text)',
  fontSize: 13, width: '100%',
  outline: 'none',
};

const ghostBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '6px 14px', borderRadius: 7,
  border: '1px solid var(--border)',
  background: 'transparent', cursor: 'pointer',
  fontSize: 12, color: 'var(--color-muted)',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600,
  color: 'var(--color-muted)', textTransform: 'uppercase',
  letterSpacing: '0.06em', marginBottom: 6,
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}
